import React, { useContext, useEffect, useState } from "react"
import Button from "../../ui/Button"
import InputFieldWithLabel from "../../Admin/Inputs/InputFieldWithLabel"
import { AdminContext } from "../../../contexts/AdminContext"
import isValidEmail from "../../../utils/isValidEmail"
import api from "../../../api2"
import Alert from "../../ui/Alert"
import ILineItem from "../../../interfaces/ILineItem"
import { mapLineItemToParams } from "../../../api2/orders"
import IOrder from "../../../interfaces/IOrder"
import eSendingMode from "../../../enums/eSendingMode"
import { InformationCircleIcon } from "@heroicons/react/20/solid"
import isValidPhoneNumber from "../../../utils/isValidPhoneNumber"

interface ComponentProps {
  email: string
  onSuccess: () => void
  order: IOrder
  campingStyleId: string | undefined
  isTaxFeeEnabled: boolean
  isCreditCardFeeEnabled: boolean
  discountSlug: string
  lineItems: Array<Partial<ILineItem>>
  depositLineItems: Array<Partial<ILineItem>>
  isNewReservation?: boolean
  width?: string
  phone?: string
  selectedSendingMode?: eSendingMode
  isStore?: boolean
}

const PaymentLinkForm = (props: ComponentProps) => {
  const { camp, showNotification } = useContext(AdminContext)

  const [email, setEmail] = useState(props.email)
  const [errorMessage, setErrorMessage] = useState("")
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    setEmail(props.email)
  }, [props.email])

  const handleChangeEmail = (e) => {
    setEmail(e.target.value)
  }

  const handleSubmit = async () => {
    setErrorMessage(null)
    setIsLoading(true)

    api.Orders.createPaymentLink({
      order_id: props.order?.id,
      camp_id: camp.id,
      camper_id: props.order?.camper_id,
      camping_style_id: props.campingStyleId,
      is_credit_card_fee_enabled: props.isCreditCardFeeEnabled,
      discount_slug: props.discountSlug,
      is_store: props.isStore,
      line_items_attributes: props.lineItems.map((li) => mapLineItemToParams(li, props.isTaxFeeEnabled)),
      partial_line_items_attributes: props.depositLineItems?.map((li) =>
        mapLineItemToParams(li, props.isTaxFeeEnabled),
      ),
      email,
    })
      .then(({ payment_link }) => {
        // TODO: update local order from payment_link.order
        props.onSuccess()
        setIsLoading(false)
      })
      .catch((err) => {
        setErrorMessage(err.message)
        showNotification({ type: "error", title: err.message })
        setIsLoading(false)
      })
  }

  return (
    <div className="flex flex-col">
      {props.isNewReservation ? (
        <div className="flex items-center mb-0.75">
          <InformationCircleIcon className="h-5 w-5 text-gray-400 mr-1" />
          {props.selectedSendingMode === eSendingMode.EMAIL_AND_TEXT &&
          isValidEmail(props.email) &&
          isValidPhoneNumber(props.phone) ? (
            <div className="text-sm text-gray-500">
              Will send a payment link to {props.email} and {props.phone}
            </div>
          ) : props.selectedSendingMode === eSendingMode.EMAIL && isValidEmail(props.email) ? (
            <div className="text-sm text-gray-500">Will send a payment link to {props.email}</div>
          ) : props.selectedSendingMode === eSendingMode.TEXT && isValidPhoneNumber(props.phone) ? (
            <div className="text-sm text-gray-500">Will send a payment link to {props.phone}</div>
          ) : props.selectedSendingMode === eSendingMode.NONE ? (
            <div className="text-sm text-gray-500">To send a payment link, you must send a confirmation message</div>
          ) : (
            <div className="text-sm text-gray-500">
              Please add a valid phone or email in the guest info in order to send a payment link
            </div>
          )}
        </div>
      ) : (
        <InputFieldWithLabel
          className={props.width ? props.width : "w-full"}
          value={email}
          onChange={handleChangeEmail}
          label="Email"
        />
      )}
      {!!errorMessage && (
        <div className="mt-4">
          <Alert description={errorMessage} />
        </div>
      )}
      {!props.isNewReservation && (
        <div className="mt-4 flex justify-end">
          <Button
            onClick={handleSubmit}
            className="px-6"
            variant="blue"
            disabled={!isValidEmail(email) || isLoading || Number(props.order?.total_in_cents) <= 0}
          >
            Send payment link
          </Button>
        </div>
      )}
    </div>
  )
}

export default PaymentLinkForm
